// landing/sections/GetTheAppSection.tsx — the Android download card.
//
// The APK is distributed from this site directly (Play is deferred — owner
// ruling, 2026-08-22), so this card is honest about the sideload step instead
// of pretending to be a store badge. The anchor id is the target of the hero's
// "Get the Android app" CTA and of external links to /#get-the-app.

import styled from 'styled-components';
import { ConsolePanel, MetaRow } from '@/ui';
import { Icon } from '@/icons';
import { track } from '@/core/lib/analytics';
import { APK_DOWNLOAD_URL, APK_SIZE_LABEL, APK_VERSION } from '../constants';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Anchor landing offset: keep the heading clear of the topbar when the hero's
     #get-the-app link scrolls here. */
  scroll-margin-top: 72px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Card = styled(ConsolePanel)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 20px;
`;

const AppMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.accentCyan};
  flex-shrink: 0;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 220px;
  min-width: 0;
`;

const AppName = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AppFacts = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-decoration: none;
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  transition: transform 80ms ease;
  &:active { transform: translateY(1px); }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const SmallPrint = styled.p`
  margin: 0;
  flex-basis: 100%;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textFaint};
`;

export function GetTheAppSection() {
  return (
    <Wrap id="get-the-app" aria-labelledby="landing-app-heading">
      <SectionHeading id="landing-app-heading">Get the Android app</SectionHeading>
      <Card>
        <AppMark aria-hidden="true">
          <Icon name="signal" size={28} />
        </AppMark>
        <Meta>
          <AppName>TruXSocial for Android</AppName>
          <AppFacts>
            <MetaRow>
              <span>Version {APK_VERSION}</span>
              <span>{APK_SIZE_LABEL}</span>
              <span>Signed by Nicholic</span>
            </MetaRow>
          </AppFacts>
        </Meta>
        <DownloadLink
          href={APK_DOWNLOAD_URL}
          download
          onClick={() => track('apk-downloaded')}
        >
          <Icon name="download" size={16} />
          Download APK
        </DownloadLink>
        <SmallPrint>
          Downloaded directly from this site, not an app store. Android will ask you to
          allow installs from your browser the first time. It is not on Google Play yet.
          iPhone isn&apos;t supported yet; the website works on every phone.
        </SmallPrint>
      </Card>
    </Wrap>
  );
}
